"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Ship,
  Download,
  Package,
  Truck,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import { ShippingTypeSelectionModal } from "@/components/modals/ShippingTypeSelectionModal";
import { ManualShippingDetailsModal } from "@/components/modals/ManualShippingDetailsModal";
import { InTransitModal } from "@/components/modals/InTransitModal";
import { ShippingProviderModal } from "@/components/modals/ShippingProviderModal";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { Order, OrderSet, ShippingType } from "@/types/models/order";

interface OrderSetActionsBarProps {
  orderSet: OrderSet;
  order?: Order | null;
  onUpdate: () => void;
}

interface ShippingProvider {
  id: string;
  partnerName: string;
  rating: number;
  deliveryCharge: number;
  shipmentType: "AIR" | "SURFACE";
  rtoCost: number;
  expectedPickup: string;
  expectedDelivery: string;
  chargeableWeight: number;
  courierOptionId?: string;
  correlationId?: string;
}

export function OrderSetActionsBar({
  orderSet,
  order,
  onUpdate,
}: OrderSetActionsBarProps) {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();

  const [showShippingTypeModal, setShowShippingTypeModal] = useState(false);
  const [showManualShippingModal, setShowManualShippingModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showInTransitModal, setShowInTransitModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const shippingStatus = orderSet?.current_shipping_stage;
  const isManualShipping = orderSet?.shipping_type === ShippingType.EXTERNAL;

  // Handle CREATED stage - Ship Now
  const handleShipNow = () => {
    setShowShippingTypeModal(true);
  };

  const handleShippingTypeSelect = (type: ShippingType) => {
    setShowShippingTypeModal(false);
    if (type === ShippingType.EXTERNAL) {
      setShowManualShippingModal(true);
    } else {
      setShowProviderModal(true);
    }
  };

  const handleManualShippingSubmit = async (data: {
    awb_code: string;
    courier_name: string;
    awb_file_url: string;
    tracking_url?: string;
  }) => {
    setLoading(true);
    try {
      let response;
      if (order) {
        response = await apiClient.post(
          `/api/v1/admin/:storeId/order/${order.id}/set/${orderSet.id}/dispatch-manual`,
          {
            awb_code: data.awb_code,
            courier_name: data.courier_name,
            awb_file_url: data.awb_file_url,
            tracking_id: data.tracking_url,
            pickup_location_id: orderSet.pickup_address_id,
          },
        );
      }

      if (response && response.valid) {
        toast.success(
          "Order set dispatched successfully with external shipping",
        );
        setShowManualShippingModal(false);
        onUpdate();
      } else {
        toast.error(response?.message || "Failed to dispatch order set");
      }
    } catch (error) {
      console.error("Error dispatching order set:", error);
      toast.error("Failed to dispatch order set");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = async (provider: ShippingProvider) => {
    setLoading(true);
    try {
      let response;
      if (order) {
        response = await apiClient.post(
          `/api/v1/admin/:storeId/order/${order.id}/set/${orderSet.id}/dispatch-automatic`,
          {
            courier_option_id:
              provider.courierOptionId || provider.correlationId,
          },
        );
      }

      if (response && response.valid) {
        toast.success(
          "Order set dispatched successfully with internal shipping",
        );
        setShowProviderModal(false);
        onUpdate();
      } else {
        toast.error(response?.message || "Failed to dispatch order set");
      }
    } catch (error) {
      console.error("Error dispatching order set:", error);
      toast.error("Failed to dispatch order set");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      const response = await apiClient.get(
        `/api/v1/admin/:storeId/order-sets/${orderSet.id}/shipping-label`,
        { responseType: "blob" },
      );

      if (response) {
        const url = window.URL.createObjectURL(new Blob([response as any]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `shipping-label-${orderSet.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Shipping label downloaded");
      }
    } catch (error) {
      console.error("Error downloading label:", error);
      toast.error("Failed to download shipping label");
    }
  };

  const handlePickedUp = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(
        `/api/v1/admin/:storeId/order-sets/${orderSet.id}/mark-picked-up`,
      );

      if (response && response.valid) {
        toast.success("Order set marked as picked up");
        onUpdate();
      } else {
        toast.error(response?.message || "Failed to mark as picked up");
      }
    } catch (error) {
      console.error("Error marking as picked up:", error);
      toast.error("Failed to mark as picked up");
    } finally {
      setLoading(false);
    }
  };

  const handleInTransit = () => {
    setShowInTransitModal(true);
  };

  const handleInTransitSubmit = async (data: {
    location: string;
    estimated_delivery_date: string;
    notes?: string;
  }) => {
    setLoading(true);
    try {
      const response = await apiClient.post(
        `/api/v1/admin/:storeId/order-sets/${orderSet.id}/mark-in-transit`,
        data,
      );

      if (response && response.valid) {
        toast.success("Order set marked as in transit");
        setShowInTransitModal(false);
        onUpdate();
      } else {
        toast.error(response?.message || "Failed to mark as in transit");
      }
    } catch (error) {
      console.error("Error marking as in transit:", error);
      toast.error("Failed to mark as in transit");
    } finally {
      setLoading(false);
    }
  };

  const handleDelivered = async () => {
    if (
      !confirm("Are you sure you want to mark this order set as delivered?")
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post(
        `/api/v1/admin/:storeId/order-sets/${orderSet.id}/mark-delivered`,
      );

      if (response && response.valid) {
        toast.success("Order set marked as delivered");
        onUpdate();
      } else {
        toast.error(response?.message || "Failed to mark as delivered");
      }
    } catch (error) {
      console.error("Error marking as delivered:", error);
      toast.error("Failed to mark as delivered");
    } finally {
      setLoading(false);
    }
  };

  const handleContactCustomer = () => {
    const customerId = order?.user_id;
    if (customerId) {
      navigate(
        `/${storeType}/${storeId}/marketing/campaign?customerId=${customerId}&action=create`,
      );
    } else {
      toast.error("Customer ID not found");
    }
  };

  // Render actions based on shipping_status
  const renderActionsByStage = () => {
    switch (shippingStatus) {
      case "CREATED":
        return (
          <Button
            onClick={handleShipNow}
            disabled={loading}
            className="bg-primary hover:bg-primary/90"
          >
            <Ship className="h-4 w-4 mr-2" />
            Ship Now
          </Button>
        );

      case "CONFIRMED":
        return (
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadLabel}
              disabled={loading}
              variant="outline"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Label
            </Button>
            <Button
              onClick={handleShipNow}
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
            >
              <Ship className="h-4 w-4 mr-2" />
              Create Shipment
            </Button>
            {isManualShipping && (
              <Button
                onClick={handlePickedUp}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Package className="h-4 w-4 mr-2" />
                Mark as Picked Up
              </Button>
            )}
          </div>
        );

      case "SHIPPING":
      case "DISPATCHED":
      case "PICKED_UP":
      case "IN_TRANSIT":
      case "OUT_FOR_DELIVERY":
        return isManualShipping ? (
          <div className="flex gap-2">
            {!["IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(
              shippingStatus || "",
            ) && (
              <Button
                onClick={handleInTransit}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Truck className="h-4 w-4 mr-2" />
                Mark In Transit
              </Button>
            )}
            <Button
              onClick={handleDelivered}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark as Delivered
            </Button>
          </div>
        ) : null;

      case "COMPLETED":
        return (
          <Button
            onClick={handleContactCustomer}
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Contact Customer
          </Button>
        );

      default:
        return (
          <p className="text-sm text-muted-foreground">
            No further status updates available
          </p>
        );
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">{renderActionsByStage()}</div>

      {/* Modals */}
      <ShippingTypeSelectionModal
        isOpen={showShippingTypeModal}
        onClose={() => setShowShippingTypeModal(false)}
        onSelect={handleShippingTypeSelect}
      />

      <ManualShippingDetailsModal
        isOpen={showManualShippingModal}
        onClose={() => setShowManualShippingModal(false)}
        onSubmit={handleManualShippingSubmit}
        loading={loading}
      />

      <ShippingProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onSelectProvider={handleProviderSelect}
        storeId={storeId}
        orderId={order?.id}
        orderSetId={orderSet?.id}
        shipmentProviderMode="seller"
      />

      <InTransitModal
        isOpen={showInTransitModal}
        onClose={() => setShowInTransitModal(false)}
        onSubmit={handleInTransitSubmit}
        loading={loading}
      />
    </>
  );
}
